import { MultiSelectPopup } from '@/components/filtros/MultiSelectPopup';
import { useCostoM2Matriz } from '@/hooks/useAvanceMatriz';
import { useFiltrosGlobales } from '@/lib/FiltrosGlobalesContext';
import type { MatrizCostoM2Fila } from '@/lib/cliente';
import { exportarPivotExcel } from '@/lib/exportarPivotExcel';
import { CaretDown, CaretRight, CircleNotch, DownloadSimple } from '@phosphor-icons/react';
import { type ReactElement, useMemo, useState } from 'react';

/** Tarea padre = prefijo del task_no antes del primer punto ("1.1" → "1"). */
const padreDeTask = (taskNo: string) => {
  const i = taskNo.indexOf('.');
  return i < 0 ? taskNo : taskNo.slice(0, i);
};

/**
 * Matriz Costo/m² × Partida (viviendas, tareas padre 1-4).
 *  - Filas: Proyecto → Bloque → Obra (árbol colapsable, subtotales + Gran Total).
 *  - Columnas: Tarea Padre → Partida (grupos de columna colapsables) + Total.
 *  - Celda = Σcosto ÷ Σm² (cociente ponderado; correcto a cualquier nivel).
 *  - Variante: General (directo) / Mano de Obra / Materiales (Dir − MO).
 *  - Denominador: m² construidos o m² totales.
 */
type Variante = 'general' | 'mo' | 'materiales';
type Denominador = 'construidos' | 'totales';

function fmtNum(v: number): string {
  if (Number.isNaN(v) || !v) return '—';
  const neg = v < 0;
  const s = Math.round(Math.abs(v))
    .toLocaleString('es-CR', { maximumFractionDigits: 0 })
    .replace(/,/g, ' ');
  return `${neg ? '-' : ''}${s}`;
}
function colorHeatmap(intensidad: number): string {
  if (Number.isNaN(intensidad)) return '';
  const a = 0.08 + Math.min(1, Math.max(0, intensidad)) * 0.45;
  return `rgba(59, 130, 246, ${a.toFixed(3)})`;
}

interface ObraAgg {
  worksNo: string;
  display: string | null;
  proy: string;
  bloque: string;
  m2c: number;
  m2t: number;
  porTask: Map<string, { dir: number; mo: number }>;
}

export function MatrizCostoM2(): ReactElement {
  const { data, isLoading, error } = useCostoM2Matriz();
  const [variante, setVariante] = useState<Variante>('general');
  const [denominador, setDenominador] = useState<Denominador>('construidos');
  const [padresExp, setPadresExp] = useState<Set<string>>(new Set());
  // Se guardan las ramas COLAPSADAS (vacío = todo expandido).
  const [colapsadas, setColapsadas] = useState<Set<string>>(new Set());
  // Filtros (como en Costos/m²): solo obras al 100% + Tarea Padre / Partida.
  const [solo100, setSolo100] = useState(false);
  const [padresSel, setPadresSel] = useState<Set<string>>(new Set());
  const [partidasSel, setPartidasSel] = useState<Set<string>>(new Set());

  const rows: MatrizCostoM2Fila[] = useMemo(() => data?.data ?? [], [data]);

  // Grupos de columna: Tarea Padre → hojas (subpartidas).
  const grupos = useMemo(() => {
    const m = new Map<string, { padreDisplay: string; hojas: Map<string, string> }>();
    for (const r of rows) {
      let g = m.get(r.tareaPadre);
      if (!g) {
        g = { padreDisplay: r.tareaPadreDisplay ?? r.tareaPadre, hojas: new Map() };
        m.set(r.tareaPadre, g);
      }
      g.hojas.set(r.taskNo, r.tareaDisplay ?? r.taskNo);
    }
    return [...m.entries()]
      .sort((a, b) => a[0].localeCompare(b[0], 'es', { numeric: true }))
      .map(([padre, g]) => ({
        padre,
        padreDisplay: g.padreDisplay,
        hojas: [...g.hojas.entries()]
          .sort((a, b) => a[0].localeCompare(b[0], 'es', { numeric: true }))
          .map(([taskNo, display]) => ({ taskNo, display })),
      }));
  }, [rows]);

  // Índice por obra.
  const obras = useMemo(() => {
    const m = new Map<string, ObraAgg>();
    for (const r of rows) {
      let o = m.get(r.worksNo);
      if (!o) {
        o = {
          worksNo: r.worksNo,
          display: r.displayName,
          proy: r.bloque ?? '(sin proyecto)',
          bloque: r.subBloque ?? '(sin bloque)',
          m2c: r.m2Construidos,
          m2t: r.areaM2Total,
          porTask: new Map(),
        };
        m.set(r.worksNo, o);
      }
      o.porTask.set(r.taskNo, { dir: r.costoDir, mo: r.costoMO });
    }
    return m;
  }, [rows]);

  // Filtro global % Avance min/max (pctAvance = m²construidos / m²totales).
  const { filtros: filtrosGlobales } = useFiltrosGlobales();
  const avMin = filtrosGlobales.pctAvanceMin ?? 0;
  const avMax = filtrosGlobales.pctAvanceMax ?? 100;
  const filtrarAvance = avMin > 0 || avMax < 100;

  // "Solo al 100%": obra completa = m²construidos/m²totales ≈ 100% con área > 0.
  const obrasVisibles = useMemo(
    () =>
      [...obras.values()].filter((o) => {
        if (solo100 && !(o.m2t > 0 && o.m2c / o.m2t >= 0.9995)) return false;
        if (filtrarAvance) {
          const pct = o.m2t > 0 ? (o.m2c / o.m2t) * 100 : 0;
          if (pct < avMin || pct > avMax) return false;
        }
        return true;
      }),
    [obras, solo100, filtrarAvance, avMin, avMax],
  );

  // Filtros de columna (Tarea Padre → Partida), multi-selección.
  const padresOpts = useMemo(
    () => grupos.map((g) => ({ value: g.padre, label: g.padreDisplay })),
    [grupos],
  );
  const partidasEfectivas = useMemo(
    () => new Set([...partidasSel].filter((t) => padresSel.has(padreDeTask(t)))),
    [partidasSel, padresSel],
  );
  const partidasOpts = useMemo(() => {
    if (padresSel.size === 0) return [];
    return grupos
      .filter((g) => padresSel.has(g.padre))
      .flatMap((g) => g.hojas.map((h) => ({ value: h.taskNo, label: h.display })));
  }, [grupos, padresSel]);
  const gruposMostrar = useMemo(
    () => (padresSel.size > 0 ? grupos.filter((g) => padresSel.has(g.padre)) : grupos),
    [grupos, padresSel],
  );
  // Hojas a usar de un grupo: si hay partidas seleccionadas de ese padre, solo esas.
  const hojasDe = (g: { padre: string; hojas: { taskNo: string; display: string }[] }) => {
    const sel = g.hojas.filter((h) => partidasEfectivas.has(h.taskNo));
    return sel.length > 0 ? sel : g.hojas;
  };
  const togglePadreFiltro = (v: string) =>
    setPadresSel((prev) => {
      const n = new Set(prev);
      if (n.has(v)) n.delete(v);
      else n.add(v);
      return n;
    });
  const togglePartidaFiltro = (v: string) =>
    setPartidasSel((prev) => {
      const n = new Set(prev);
      if (n.has(v)) n.delete(v);
      else n.add(v);
      return n;
    });
  const limpiarFiltros = () => {
    setPadresSel(new Set());
    setPartidasSel(new Set());
  };

  // Árbol Proyecto → Bloque → Obra.
  const arbol = useMemo(() => {
    const proyectos = new Map<
      string,
      { proyecto: string; bloques: Map<string, { bloque: string; key: string; obras: ObraAgg[] }> }
    >();
    for (const o of obrasVisibles) {
      let p = proyectos.get(o.proy);
      if (!p) {
        p = { proyecto: o.proy, bloques: new Map() };
        proyectos.set(o.proy, p);
      }
      const bkey = `${o.proy}|${o.bloque}`;
      let b = p.bloques.get(bkey);
      if (!b) {
        b = { bloque: o.bloque, key: bkey, obras: [] };
        p.bloques.set(bkey, b);
      }
      b.obras.push(o);
    }
    const arr = [...proyectos.values()].sort((a, b) => a.proyecto.localeCompare(b.proyecto));
    for (const p of arr)
      for (const b of p.bloques.values())
        b.obras.sort((x, y) => x.worksNo.localeCompare(y.worksNo));
    return arr;
  }, [obrasVisibles]);

  const costoTask = (agg: { dir: number; mo: number } | undefined): number => {
    if (!agg) return 0;
    if (variante === 'mo') return agg.mo;
    if (variante === 'materiales') return agg.dir - agg.mo;
    return agg.dir;
  };
  const m2De = (o: ObraAgg) => (denominador === 'totales' ? o.m2t : o.m2c);

  // Columnas hoja visibles (según expansión de padres + filtros) + Total.
  // Cálculo directo (datos chicos) para no pelear con deps de `hojasDe`.
  const cols: {
    key: string;
    padre: string;
    padreDisplay: string;
    etiqueta: string;
    taskNos: string[];
  }[] = [];
  for (const g of gruposMostrar) {
    const hojas = hojasDe(g);
    if (padresExp.has(g.padre)) {
      for (const h of hojas)
        cols.push({
          key: `${g.padre}|${h.taskNo}`,
          padre: g.padre,
          padreDisplay: g.padreDisplay,
          etiqueta: h.display,
          taskNos: [h.taskNo],
        });
    } else {
      cols.push({
        key: `${g.padre}|__all`,
        padre: g.padre,
        padreDisplay: g.padreDisplay,
        etiqueta: 'Total',
        taskNos: hojas.map((h) => h.taskNo),
      });
    }
  }
  const totalTaskNos = gruposMostrar.flatMap((g) => hojasDe(g).map((h) => h.taskNo));

  // Costo/m² ponderado para un conjunto de obras y un conjunto de task_no.
  const celda = (lista: ObraAgg[], taskNos: string[]): number => {
    let num = 0;
    let den = 0;
    for (const o of lista) {
      den += m2De(o);
      for (const t of taskNos) num += costoTask(o.porTask.get(t));
    }
    return den > 0 ? num / den : Number.NaN;
  };

  // Heatmap: máximo por columna sobre obras hoja. Cálculo directo (datos chicos:
  // ~obras × columnas); depende de variante/denominador vía `celda`.
  const maxCol = (() => {
    const m = new Map<string, number>();
    for (const o of obrasVisibles) {
      for (const c of cols) {
        const v = Math.abs(celda([o], c.taskNos));
        if (!Number.isNaN(v)) m.set(c.key, Math.max(m.get(c.key) ?? 0, v));
      }
    }
    return m;
  })();

  const abierto = (key: string) => !colapsadas.has(key);
  const toggleFila = (key: string) =>
    setColapsadas((prev) => {
      const n = new Set(prev);
      if (n.has(key)) n.delete(key);
      else n.add(key);
      return n;
    });
  const togglePadre = (padre: string) =>
    setPadresExp((prev) => {
      const n = new Set(prev);
      if (n.has(padre)) n.delete(padre);
      else n.add(padre);
      return n;
    });
  const todasFilasKeys = useMemo(() => {
    const k = new Set<string>();
    for (const p of arbol) {
      k.add(`P|${p.proyecto}`);
      for (const b of p.bloques.values()) k.add(`B|${b.key}`);
    }
    return k;
  }, [arbol]);

  const tituloVariante =
    variante === 'mo' ? 'Mano de Obra' : variante === 'materiales' ? 'Materiales' : 'General';

  // Agrupaciones de encabezado (padre → colspan) para la fila 1.
  const gruposHeader: { padre: string; padreDisplay: string; span: number }[] = [];
  for (const c of cols) {
    const ult = gruposHeader[gruposHeader.length - 1];
    if (ult && ult.padre === c.padre) ult.span += 1;
    else gruposHeader.push({ padre: c.padre, padreDisplay: c.padreDisplay, span: 1 });
  }

  const exportar = () => {
    const columnasExport = [
      ...cols.map((c) => ({
        etiquetaGrupo: c.padreDisplay,
        etiquetaMetrica: c.etiqueta,
        formato: 'numero' as const,
      })),
      { etiquetaGrupo: 'Total', etiquetaMetrica: 'Costo/m²', formato: 'numero' as const },
    ];
    const celdasDe = (lista: ObraAgg[]) => [
      ...cols.map((c) => {
        const v = celda(lista, c.taskNos);
        const heat = Number.isNaN(v) ? null : Math.abs(v) / (maxCol.get(c.key) || 1);
        return { valor: v, heat };
      }),
      { valor: celda(lista, totalTaskNos), heat: null },
    ];
    const filas: {
      depth: number;
      etiqueta: string;
      esSubtotal: boolean;
      esGranTotal?: boolean;
      celdas: { valor: number; heat: number | null }[];
    }[] = [];
    const todas: ObraAgg[] = [];
    for (const p of arbol) {
      const obrasP = [...p.bloques.values()].flatMap((b) => b.obras);
      todas.push(...obrasP);
      filas.push({ depth: 0, etiqueta: p.proyecto, esSubtotal: true, celdas: celdasDe(obrasP) });
      for (const b of p.bloques.values()) {
        filas.push({ depth: 1, etiqueta: b.bloque, esSubtotal: true, celdas: celdasDe(b.obras) });
        for (const o of b.obras) {
          const etiqueta =
            o.display && o.display !== o.worksNo ? `${o.worksNo} — ${o.display}` : o.worksNo;
          filas.push({ depth: 2, etiqueta, esSubtotal: false, celdas: celdasDe([o]) });
        }
      }
    }
    filas.push({
      depth: 0,
      etiqueta: 'Gran Total',
      esSubtotal: false,
      esGranTotal: true,
      celdas: celdasDe(todas),
    });
    exportarPivotExcel({
      nombreArchivo: `costo-m2-partida-${variante}`,
      tituloHoja: `Costo m2 x Partida (${tituloVariante})`,
      encabezadoJerarquia: 'Proyecto / Bloque / Obra',
      columnas: columnasExport,
      filas,
      agregacion: 'Suma',
    });
  };

  if (isLoading) {
    return (
      <div className="flex items-center gap-2 text-muted-foreground">
        <CircleNotch className="h-4 w-4 animate-spin" weight="bold" /> Cargando…
      </div>
    );
  }
  if (error) {
    return <p className="text-destructive text-sm">Error al cargar: {error.message}</p>;
  }

  // Construir filas del árbol respetando expansión.
  const filasRender: ReactElement[] = [];
  const anchoCol = { minWidth: 240 };
  const celdaValor = (lista: ObraAgg[], esSubtotal: boolean) => [
    ...cols.map((c) => {
      const v = celda(lista, c.taskNos);
      const intensidad =
        esSubtotal || Number.isNaN(v) ? Number.NaN : Math.abs(v) / (maxCol.get(c.key) || 1);
      const bg = colorHeatmap(intensidad);
      return (
        <td
          key={c.key}
          className="whitespace-nowrap border-b border-l border-zinc-100 px-2 py-1 text-right text-xs tabular-nums"
          style={bg ? { backgroundColor: bg } : undefined}
        >
          {fmtNum(v)}
        </td>
      );
    }),
    <td
      key="__total"
      className="whitespace-nowrap border-b border-l border-zinc-200 px-2 py-1 text-right text-xs font-medium tabular-nums"
      style={{ backgroundColor: 'rgba(16,185,129,0.08)' }}
    >
      {fmtNum(celda(lista, totalTaskNos))}
    </td>,
  ];

  for (const p of arbol) {
    const pKey = `P|${p.proyecto}`;
    const pOpen = abierto(pKey);
    const obrasP = [...p.bloques.values()].flatMap((b) => b.obras);
    filasRender.push(
      <tr key={pKey} className="bg-emerald-100 font-semibold">
        <td
          className="sticky left-0 z-10 border-b border-r border-zinc-200 bg-emerald-100 px-2 py-1 text-xs"
          style={anchoCol}
        >
          <button
            type="button"
            onClick={() => toggleFila(pKey)}
            className="flex items-center gap-1 hover:underline"
          >
            {pOpen ? (
              <CaretDown className="h-3 w-3" weight="bold" />
            ) : (
              <CaretRight className="h-3 w-3" weight="bold" />
            )}
            {p.proyecto}
          </button>
        </td>
        {celdaValor(obrasP, true)}
      </tr>,
    );
    if (!pOpen) continue;
    for (const b of p.bloques.values()) {
      const bKey = `B|${b.key}`;
      const bOpen = abierto(bKey);
      filasRender.push(
        <tr key={bKey} className="bg-emerald-50 font-medium">
          <td
            className="sticky left-0 z-10 border-b border-r border-zinc-200 bg-emerald-50 px-2 py-1 text-xs"
            style={anchoCol}
          >
            <button
              type="button"
              onClick={() => toggleFila(bKey)}
              className="flex items-center gap-1 pl-4 hover:underline"
            >
              {bOpen ? (
                <CaretDown className="h-3 w-3" weight="bold" />
              ) : (
                <CaretRight className="h-3 w-3" weight="bold" />
              )}
              {b.bloque}
            </button>
          </td>
          {celdaValor(b.obras, true)}
        </tr>,
      );
      if (!bOpen) continue;
      for (const o of b.obras) {
        filasRender.push(
          <tr key={`O|${o.worksNo}`} className="hover:bg-zinc-50">
            <td
              className="sticky left-0 z-10 border-b border-r border-zinc-200 bg-white px-2 py-1 text-xs"
              style={anchoCol}
            >
              <div className="pl-9">
                <div className="font-medium">{o.worksNo}</div>
                {o.display && o.display !== o.worksNo && (
                  <div className="max-w-[180px] truncate text-[10px] text-muted-foreground">
                    {o.display}
                  </div>
                )}
              </div>
            </td>
            {celdaValor([o], false)}
          </tr>,
        );
      }
    }
  }
  const todasObras = obrasVisibles;

  return (
    <div className="space-y-3">
      {/* Controles */}
      <div className="flex flex-wrap items-center gap-3 text-xs">
        <div className="inline-flex overflow-hidden rounded-md border border-zinc-300">
          {(['general', 'mo', 'materiales'] as const).map((v) => (
            <button
              key={v}
              type="button"
              onClick={() => setVariante(v)}
              className={`px-2.5 py-1 ${variante === v ? 'bg-emerald-700 text-white' : 'bg-white text-zinc-600 hover:bg-zinc-100'}`}
            >
              {v === 'general' ? 'General' : v === 'mo' ? 'Mano de Obra' : 'Materiales'}
            </button>
          ))}
        </div>
        <div className="inline-flex overflow-hidden rounded-md border border-zinc-300">
          <button
            type="button"
            onClick={() => setDenominador('construidos')}
            className={`px-2.5 py-1 ${denominador === 'construidos' ? 'bg-emerald-700 text-white' : 'bg-white text-zinc-600 hover:bg-zinc-100'}`}
          >
            m² construidos
          </button>
          <button
            type="button"
            onClick={() => setDenominador('totales')}
            className={`px-2.5 py-1 ${denominador === 'totales' ? 'bg-emerald-700 text-white' : 'bg-white text-zinc-600 hover:bg-zinc-100'}`}
          >
            m² totales
          </button>
        </div>
        <span className="text-zinc-300">|</span>
        <button
          type="button"
          onClick={() => setColapsadas(new Set())}
          className="text-blue-600 hover:underline"
        >
          Expandir todo
        </button>
        <button
          type="button"
          onClick={() => setColapsadas(new Set(todasFilasKeys))}
          className="text-blue-600 hover:underline"
        >
          Colapsar
        </button>
        <span className="ml-auto">
          <button
            type="button"
            onClick={exportar}
            className="inline-flex items-center gap-1 rounded-md border border-emerald-700 bg-white px-2 py-1 font-medium text-emerald-700 hover:bg-emerald-50"
            title="Exportar la matriz a Excel"
          >
            <DownloadSimple className="h-3.5 w-3.5" weight="bold" />
            Exportar a Excel
          </button>
        </span>
      </div>

      {/* Filtros: Solo al 100% + Tarea Padre / Partida (enfocan columnas) */}
      <div className="flex flex-wrap items-center gap-2 text-xs">
        <label
          className="flex items-center gap-1.5 text-zinc-600"
          title="Mostrar solo obras con 100% de avance"
        >
          <input type="checkbox" checked={solo100} onChange={(e) => setSolo100(e.target.checked)} />
          Solo al 100%
        </label>
        <span className="text-zinc-300">|</span>
        <span className="text-muted-foreground">Partida:</span>
        <MultiSelectPopup
          titulo="Tarea Padre"
          opciones={padresOpts}
          seleccion={padresSel}
          onToggle={togglePadreFiltro}
          onNinguna={limpiarFiltros}
        />
        <MultiSelectPopup
          titulo="Partida"
          opciones={partidasOpts}
          seleccion={partidasEfectivas}
          onToggle={togglePartidaFiltro}
          onTodas={() => setPartidasSel(new Set(partidasOpts.map((o) => o.value)))}
          onNinguna={() => setPartidasSel(new Set())}
          disabled={padresSel.size === 0}
          ancho="w-80"
        />
        {(padresSel.size > 0 || partidasEfectivas.size > 0 || solo100) && (
          <button
            type="button"
            onClick={() => {
              limpiarFiltros();
              setSolo100(false);
            }}
            className="text-blue-600 hover:underline"
          >
            Limpiar
          </button>
        )}
      </div>

      {rows.length === 0 ? (
        <div className="rounded-lg border border-zinc-200 bg-zinc-50 p-6 text-center text-sm text-muted-foreground">
          Sin datos de partidas de vivienda (tareas padre 1–4) para los filtros actuales.
        </div>
      ) : (
        <div className="overflow-x-auto rounded-lg border border-zinc-200">
          <table className="w-full border-collapse">
            <thead>
              <tr className="bg-emerald-900 text-white">
                <th
                  rowSpan={2}
                  className="sticky left-0 z-20 border-b border-emerald-800 bg-emerald-900 px-2 py-2 text-left text-xs"
                  style={anchoCol}
                >
                  Proyecto › Bloque › Obra — Costo/m² ({tituloVariante})
                </th>
                {gruposHeader.map((g) => (
                  <th
                    key={g.padre}
                    colSpan={g.span}
                    className="whitespace-nowrap border-b border-l border-emerald-800 px-2 py-2 text-center text-xs"
                  >
                    <button
                      type="button"
                      onClick={() => togglePadre(g.padre)}
                      className="inline-flex items-center gap-1 hover:underline"
                      title={padresExp.has(g.padre) ? 'Colapsar subpartidas' : 'Ver subpartidas'}
                    >
                      {padresExp.has(g.padre) ? (
                        <CaretDown className="h-3 w-3" weight="bold" />
                      ) : (
                        <CaretRight className="h-3 w-3" weight="bold" />
                      )}
                      {g.padreDisplay}
                    </button>
                  </th>
                ))}
                <th
                  rowSpan={2}
                  className="whitespace-nowrap border-b border-l border-emerald-800 bg-emerald-950 px-2 py-2 text-center text-xs"
                >
                  Total
                </th>
              </tr>
              <tr className="bg-emerald-800 text-emerald-50">
                {cols.map((c) => (
                  <th
                    key={c.key}
                    className="max-w-[160px] truncate border-l border-emerald-700 px-2 py-1 text-right text-[11px] font-normal"
                    title={c.etiqueta}
                  >
                    {c.etiqueta}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filasRender}
              {todasObras.length > 0 && (
                <tr className="border-zinc-300 border-t-2 bg-zinc-100 font-bold">
                  <td
                    className="sticky left-0 z-10 border-r border-zinc-200 bg-zinc-100 px-2 py-1 text-xs"
                    style={anchoCol}
                  >
                    Gran Total
                  </td>
                  {celdaValor(todasObras, true)}
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
